import {ComponentProps, useEffect, useMemo, useState} from 'react';
import {Box, Stack, Typography} from '@mui/material';
import DatePicker, {Calendar, DateObject} from 'react-multi-date-picker';
import {Locale} from 'react-date-object';
import {ICalendarHoliday} from '../../../models/Calendar/ICalendarHoliday';
import {useCalendarHolidays} from './useCalendarHolidays';
import {getVisibleCalendarYears} from './calendarYears';
import {useAppTranslation} from '@mybake/ui/hooks/useAppTranslation';

export const useCalendarLocale = (): Locale => {
    const {t, i18n} = useAppTranslation();
    return useMemo(() => {
        const months = t('dates:calendar.months', {returnObjects: true}) as unknown as string[];
        const shortMonths = t('dates:calendar.shortMonths', {returnObjects: true}) as unknown as string[];
        const shortDays = t('dates:calendar.shortDays', {returnObjects: true}) as unknown as string[];
        // react-date-object locales start with Saturday, regardless of the visible week start.
        const dayKeys = ['saturday', 'sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday'] as const;
        return {
            name: 'gregorian_' + (i18n.resolvedLanguage || i18n.language || 'pl'),
            months: Array.from({length: 12}, (_, index) => [months[index], shortMonths[index]]),
            weekDays: dayKeys.map((day, index) => [t(('dates:' + day) as 'dates:saturday'), shortDays[(index + 6) % 7]]),
            digits: ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'],
            meridiems: [['AM', 'am'], ['PM', 'pm']],
        };
    }, [t, i18n.resolvedLanguage, i18n.language]);
};

type MapDays = NonNullable<ComponentProps<typeof Calendar>['mapDays']>;

const holidayMapDays = (holidays: Map<string, ICalendarHoliday>, mapDays?: MapDays, workshopDates: readonly string[] = []): MapDays => (day) => {
    const original = mapDays?.(day);
    const holiday = holidays.get(day.date.format('YYYY-MM-DD'));
    const isWorkshop = workshopDates.includes(day.date.format('YYYY-MM-DD'));
    if (!isWorkshop && !holiday && day.date.toDate().getDay() !== 0) return original;
    const selected = Array.isArray(day.selectedDate) ? day.selectedDate : [day.selectedDate];
    const isSelected = selected.some(date => date && day.isSameDate(day.date, date));
    return {
        ...original,
        title: holiday?.name || original?.title,
        style: {
            ...original?.style,
            backgroundColor: isWorkshop ? (isSelected ? '#6a1b9a' : '#ab47bc') : (isSelected ? '#b91c1c' : '#ffebee'),
            color: isSelected || isWorkshop ? '#fff' : '#b91c1c',
            borderRadius: '6px',
        },
    };
};

const HolidayLegend = () => {
    const {t} = useAppTranslation();
    return <Stack direction="row" spacing={1} alignItems="flex-start" sx={{p: 1.5, maxWidth: 260}}>
        <Box aria-hidden="true" sx={{width: 14, height: 14, mt: '3px', flexShrink: 0, bgcolor: '#ffebee', border: '1px solid #b91c1c', borderRadius: '3px'}}/>
        <Typography variant="caption" color="text.secondary">{t('dates:calendar.holiday_legend')}</Typography>
    </Stack>;
};

const WorkshopLegend = () => {
    const {t} = useAppTranslation();
    return <Stack direction="row" spacing={1} alignItems="flex-start" sx={{p: 1.5, maxWidth: 260}}>
        <Box aria-hidden="true" sx={{width: 14, height: 14, mt: '3px', flexShrink: 0, bgcolor: '#ab47bc', border: '1px solid #6a1b9a', borderRadius: '3px'}}/>
        <Typography variant="caption" color="text.secondary">{t('dates:calendar.workshop_legend')}</Typography>
    </Stack>;
};

type CalendarProps = ComponentProps<typeof Calendar> & {showHolidayLegend?: boolean};

const useCalendarView = (props: Pick<CalendarProps, 'value' | 'currentDate' | 'format' | 'numberOfMonths' | 'showOtherDays' | 'weekStartDayIndex' | 'onlyMonthPicker' | 'onlyYearPicker' | 'onMonthChange' | 'onYearChange'>, enabled: boolean) => {
    const rawValue = Array.isArray(props.value) ? props.value[0] : props.value;
    const initial = props.currentDate || (rawValue ? new DateObject({date: rawValue, format: props.format}) : new DateObject());
    const initialYear = initial.isValid ? initial.year : new Date().getFullYear();
    const initialMonth = initial.isValid ? initial.month.number : new Date().getMonth() + 1;
    const [view, setView] = useState({year: initialYear, month: initialMonth});
    useEffect(() => setView({year: initialYear, month: initialMonth}), [initialYear, initialMonth]);
    const years = getVisibleCalendarYears(view.year, view.month, props.numberOfMonths, props.showOtherDays, props.weekStartDayIndex);
    const holidays = useCalendarHolidays(years, enabled && !props.onlyMonthPicker && !props.onlyYearPicker);
    const onMonthChange = (date: DateObject) => {
        setView({year: date.year, month: date.month.number});
        props.onMonthChange?.(date);
    };
    const onYearChange = (date: DateObject) => {
        setView({year: date.year, month: date.month.number});
        props.onYearChange?.(date);
    };
    return {holidays, onMonthChange, onYearChange, year: view.year, month: view.month};
};

export const LocalizedDatePicker = ({mapDays, children, showHolidayLegend = false, workshopDates, ...props}: ComponentProps<typeof DatePicker> & {showHolidayLegend?: boolean; workshopDates?: readonly string[]}) => {
    const locale = useCalendarLocale();
    const [open, setOpen] = useState(false);
    const view = useCalendarView(props, open);
    const firstMonth = view.year * 12 + view.month - 1;
    const hasVisibleWorkshop = !props.onlyMonthPicker && !props.onlyYearPicker && workshopDates?.some(date => {
        const month = Number(date.slice(0, 4)) * 12 + Number(date.slice(5, 7)) - 1;
        return month >= firstMonth && month < firstMonth + (props.numberOfMonths || 1);
    });
    return <DatePicker weekStartDayIndex={1} {...props} locale={locale} mapDays={holidayMapDays(view.holidays, mapDays, workshopDates)}
        onMonthChange={view.onMonthChange} onYearChange={view.onYearChange}
        onOpen={() => {
            if (props.onOpen?.() === false) return false;
            setOpen(true);
        }} onClose={() => {
            if (props.onClose?.() === false) return false;
            setOpen(false);
        }}>
        {children}
        {showHolidayLegend && <HolidayLegend/>}
        {hasVisibleWorkshop && <WorkshopLegend/>}
    </DatePicker>;
};

export const LocalizedCalendar = ({mapDays, children, showHolidayLegend = false, ...props}: CalendarProps) => {
    const locale = useCalendarLocale();
    const view = useCalendarView(props, true);
    return <Calendar weekStartDayIndex={1} {...props} locale={locale} mapDays={holidayMapDays(view.holidays, mapDays)}
        onMonthChange={view.onMonthChange} onYearChange={view.onYearChange}>
        {children}
        {showHolidayLegend && <HolidayLegend/>}
    </Calendar>;
};
